import { Component, EventEmitter, inject, input, Input, OnInit, Output, signal, WritableSignal } from '@angular/core';

import { ErrorIconComponent, SpinnerComponent, TrapFocusComponent } from 'remarketing-ui-components-lib';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { BehaviorSubject, Subject } from 'rxjs';
import { ChangeStatusModalDirective } from '../change-status-modal.directive';
import { Store } from '@ngxs/store';
import {
  AuctionService,
  AuthService,
  BatchPostingService,
  BuyerDealerResult,
  InventoryManagementAuctionItemStatus,
  InventoryManagementSearchResult,
  LaunchDarklyService,
  ReservationRequestDto,
  ReservationResponseDto,
  ReservationsService
} from 'remarketing-angular-library';
import { take, takeUntil } from 'rxjs/operators';
import { get } from 'lodash';
import { SearchBoxInputComponent } from '../../search-box-input/search-box-input.component';
import { AnalyticsService } from 'smartauction-frontend-micro-library';

@Component({
  selector: 'app-make-reserve-modal',
  imports: [
    SpinnerComponent,
    ErrorIconComponent,
    TranslocoDirective,
    TrapFocusComponent,
    SearchBoxInputComponent
],
  templateUrl: './make-reserve-modal.component.html',
  styleUrl: './make-reserve-modal.component.scss'
})
export class MakeReserveModalComponent extends ChangeStatusModalDirective implements OnInit {
  public readonly numberOfVehicles$ = input<number>(1);
  public readonly isReserved$ = input<boolean>(false);

  protected isSelectTextError$: WritableSignal<boolean> = signal(false);
  protected isError: boolean;
  protected isLoading: boolean;
  protected isIsvFileProcessing = true;
  protected dropDownFocusTrap = false;
  protected translations = {
    textHeader: '',
    textSubHeader: '',
    textDealership: '',
    textSelect: '',
    textError: '',
    selectTextError: '',
    textReservationChangesDisabled: ''
  };

  private isSelectOptionSelected: boolean;
  private selectedDealership: string;

  private readonly reservationService: ReservationsService;
  private readonly authService: AuthService;
  private readonly batchPostingService: BatchPostingService;
  private readonly analyticsService: AnalyticsService;

  get isDealerSeller(): boolean {
    return this.authService.getRoles().includes('Dealer_Seller');
  }

  get isIsvReservationHaltEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-reservationHaltEnabled', false);
  }

  get isIsvFileUploadHaltEnabled(): boolean {
    return this._launchDarklyService.getFeatureFlag('sell-fileProcessingReservationHaltEnabled', false);
  }

  constructor() {
    super();
    this.reservationService = inject(ReservationsService);
    this.authService = inject(AuthService);
    this.batchPostingService = inject(BatchPostingService);
    this.analyticsService = inject(AnalyticsService);
  }

  ngOnInit(): void {
    this.isLoading = false;
    this.getTranslations();
    this.checkIsvFileProcessingStatus();
  }

  checkIsvFileProcessingStatus(): void {
    if (this.isIsvReservationHaltEnabled) {
      this.isIsvFileProcessing = true;
    } else if (this.isIsvFileUploadHaltEnabled) {
      this.batchPostingService
        .checkReservationsEnabled()
        .pipe(take(1))
        .subscribe({
          next: value => {
            this.isIsvFileProcessing = !value.reservationEnabled;
          },
          error: error => {
            console.log(error);
          }
        });
    } else {
      this.isIsvFileProcessing = false;
    }
  }

  makeReserve() {
    this.isLoading = true;
    if (this.isIsvReservationHaltEnabled) {
      this.setReservationErrors();
    } else if (this.isIsvFileUploadHaltEnabled) {
      this.batchPostingService
        .checkReservationsEnabled()
        .pipe(take(1))
        .subscribe({
          next: value => {
            if (value.reservationEnabled) {
              this.performReservation();
            } else {
              this.setReservationErrors();
            }
          }
        });
    } else {
      this.performReservation();
    }
  }

  private performReservation() {
    const auctionItemUuids = [];
    this.selectedVehicles.forEach(vehicle => {
      // only adding ISV vehicles to the request to reserve
      if (
        this.getVehicleTypeCode(vehicle) === 'ISV' &&
        (vehicle.auctionItemStatus === 'ACTIVE' || this.isChangeReservedDealershipEnabled)
      ) {
        auctionItemUuids.push(vehicle.vehicleUuid);
      }
    });

    const payload = {
      auctionItemUuids,
      buyerOeUuid: this.selectedDealership,
      reservationStatus: 'RESERVE'
    } as ReservationRequestDto;

    if (this.selectedDealership !== undefined && !this.isSelectOptionSelected) {
      (this.isReserved$()
        ? this.reservationService.updateVehicleReservations(payload)
        : this.reservationService.postVehicleReservations(payload)
      )
        .pipe(take(1))
        .subscribe({
          next: (response: ReservationResponseDto[]) => {
            if (response) {
              this.changeReserveStatus(InventoryManagementAuctionItemStatus.RESERVED, response);
              this.isLoading = false;
              this.analyticsTrackResponseSuccessErrors(response);
            }
          },
          error: error => {
            if (error) {
              this.isLoading = false;
              this.isError = true;
            }
          }
        });
    } else {
      this.isLoading = false;
      this.isSelectTextError$.set(true);
    }
  }

  setReservationErrors() {
    this.isLoading = false;
    this.isError = true;
    this.translations.textError = this.translations.textReservationChangesDisabled;
  }

  closeModal() {
    this.isLoading = false;
    this.isError = false;
    this.close.emit();
  }

  getVehicleTypeCode(vehicle: InventoryManagementSearchResult) {
    return get(vehicle, 'vehicleTypeCode', null);
  }

  getTranslations() {
    super.getTranslations();
    const keyParams = {
      'sellModule.manageInventory.actionListModal.makeReserveModal.textHeader': {
        numberOfVehicles: this.numberOfVehicles$()
      },
      'sellModule.manageInventory.actionListModal.makeReserveModal': {},
      'app.layout': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.translations.textHeader = translate[0];
        this.translations.textSubHeader = translate[1].textSubHeader;
        this.translations.textDealership = translate[1].textDealership;
        this.translations.textSelect = translate[1].textSelect;
        this.translations.selectTextError = translate[1].selectTextError;
        this.translations.textReservationChangesDisabled = translate[1].textReservationChangesDisabled;
        this.translations.textError = translate[2].cantCompleteRequestError;
      });
  }

  analyticsTrackResponseSuccessErrors(response: ReservationResponseDto[]) {
    const uniqueErrorMessages = new Map<string, string>();
    response.forEach(item => {
      item.errors?.forEach(error => {
        if (!uniqueErrorMessages.has(error.errorCode)) {
          uniqueErrorMessages.set(error.errorCode, error.errorMessage);
        }
      });
    });
    uniqueErrorMessages.forEach((code, message) => {
      this.analyticsTrackError(code, message);
    });
  }

  analyticsTrackError(errorCode: string, errorMessage: string) {
    this.analyticsService.track('customError', errorMessage, errorCode);
  }

  onDealerSelected(buyerDealerResult: BuyerDealerResult) {
    this.selectedDealership = buyerDealerResult.oeUuid;
  }
}
